import { useMemo, useState } from 'react';
import type { MatchHistory, PlayerSample, Sample } from '../../app/matchHistory';
import { AGE_NAMES } from '../../gameplay/types';

interface PlayerInfo { id: number; name: string; color: string; civ: string; winner: boolean; defeated: boolean }

/** Food, wood, gold, stone; and the four parts of the score. Validated for the dark panel surface. */
const RES_SERIES = [
  { label: 'Food', color: '#d95926' },
  { label: 'Wood', color: '#199e70' },
  { label: 'Gold', color: '#c98500' },
  { label: 'Stone', color: '#3987e5' },
];
const SCORE_SERIES = [
  { key: 'economy', label: 'Economy', color: '#3987e5' },
  { key: 'military', label: 'Military', color: '#d95926' },
  { key: 'technology', label: 'Technology', color: '#199e70' },
  { key: 'buildings', label: 'Buildings', color: '#c98500' },
] as const;
const KILL_SERIES = [
  { label: 'Kills', color: '#3987e5' },
  { label: 'Losses', color: '#d95926' },
];

const METRICS: { key: string; label: string; get: (p: PlayerSample) => number }[] = [
  { key: 'score', label: 'Score', get: (p) => p.score.total },
  { key: 'gathered', label: 'Resources gathered', get: (p) => p.gathered.reduce((a, b) => a + b, 0) },
  { key: 'pop', label: 'Population', get: (p) => p.pop },
  { key: 'villagers', label: 'Villagers', get: (p) => p.villagers },
  { key: 'military', label: 'Military units', get: (p) => p.military },
  { key: 'buildings', label: 'Buildings', get: (p) => p.buildings },
  { key: 'kills', label: 'Kills', get: (p) => p.kills },
];

const fmt = (n: number) => Math.round(n).toLocaleString('en');
const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** Round a maximum up to a tidy axis end and give the tick step. */
function niceScale(max: number): { top: number; step: number } {
  if (max <= 0) return { top: 1, step: 1 };
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return { top: Math.ceil(max / step) * step, step };
}

interface Series { id: string; name: string; color: string; get: (s: Sample) => number }

function LineChart({ samples, series, label, width = 720 }: { samples: Sample[]; series: Series[]; label: string; width?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = width, H = Math.round(width * 0.42), L = 56, R = 96, T = 14, B = 34;
  const tEnd = Math.max(60, samples[samples.length - 1]?.t ?? 60);
  const tStart = samples[0]?.t ?? 0;
  const visible = series;
  const max = Math.max(0, ...samples.flatMap((s) => visible.map((p) => p.get(s))));
  const { top, step } = niceScale(max);
  const x = (t: number) => L + ((t - tStart) / Math.max(1, tEnd - tStart)) * (W - L - R);
  const y = (v: number) => T + (1 - v / top) * (H - T - B);
  const minuteStep = niceScale((tEnd - tStart) / 60).step;
  const xTicks: number[] = [];
  for (let m = Math.ceil(tStart / 60 / minuteStep) * minuteStep; m * 60 <= tEnd; m += minuteStep) xTicks.push(m);
  const yTicks: number[] = [];
  for (let v = 0; v <= top + 1e-9; v += step) yTicks.push(v);
  // Direct labels at the line ends, nudged apart so they never overlap.
  const ends = visible.map((p) => ({ p, v: p.get(samples[samples.length - 1]) })).sort((a, b) => b.v - a.v);
  let lastY = -Infinity;
  const labelY = ends.map((e) => {
    const yy = Math.max(y(e.v), lastY + 14);
    lastY = yy;
    return { ...e, yy };
  });
  const hs = hover !== null ? samples[hover] : null;
  const onMove = (ev: React.PointerEvent<SVGRectElement>) => {
    const box = (ev.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = ((ev.clientX - box.left) / box.width) * W;
    let best = 0;
    for (let i = 1; i < samples.length; i++) if (Math.abs(x(samples[i].t) - px) < Math.abs(x(samples[best].t) - px)) best = i;
    setHover(best);
  };
  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label={label}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="grid" />
            <text x={L - 8} y={y(v) + 4} className="axis" textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {xTicks.map((m) => <text key={m} x={x(m * 60)} y={H - 12} className="axis" textAnchor="middle">{m}m</text>)}
        <line x1={L} x2={W - R} y1={y(0)} y2={y(0)} className="baseline" />
        {visible.map((p) => (
          <polyline key={p.id} fill="none" stroke={p.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
            points={samples.map((s) => `${x(s.t).toFixed(1)},${y(p.get(s)).toFixed(1)}`).join(' ')} />
        ))}
        {visible.length <= 4 && labelY.map(({ p, yy }) => (
          <text key={p.id} x={W - R + 8} y={yy + 4} className="end-label">{p.name}</text>
        ))}
        {hs && (
          <g>
            <line x1={x(hs.t)} x2={x(hs.t)} y1={T} y2={H - B} className="crosshair" />
            {visible.map((p) => <circle key={p.id} cx={x(hs.t)} cy={y(p.get(hs))} r={4} fill={p.color} stroke="var(--panel-solid)" strokeWidth={2} />)}
          </g>
        )}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hs && (
        <div className="chart-tip" style={{ left: `${(x(hs.t) / W) * 100}%` }}>
          <div className="muted">{fmtTime(hs.t)}</div>
          {visible.map((p) => ({ p, v: p.get(hs) })).sort((a, b) => b.v - a.v).map(({ p, v }) => (
            <div key={p.id} className="row spread"><span><i className="sw" style={{ background: p.color }} />{p.name}</span><b>{fmt(v)}</b></div>
          ))}
        </div>
      )}
    </div>
  );
}

/** One horizontal bar per player, split into parts that add up to the player's total. */
function StackedBars({ title, players, series, values }: { title: string; players: PlayerInfo[]; series: { label: string; color: string }[]; values: (p: PlayerInfo) => number[] }) {
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const rows = players.map((p) => ({ p, v: values(p) })).sort((a, b) => b.v.reduce((x, y) => x + y, 0) - a.v.reduce((x, y) => x + y, 0));
  const max = Math.max(1, ...rows.map((r) => r.v.reduce((a, b) => a + b, 0)));
  return (
    <div className="chart-block">
      <div className="row spread"><h3>{title}</h3><Legend series={series} /></div>
      <div className="bars" onPointerLeave={() => setTip(null)}>
        {rows.map(({ p, v }) => {
          const total = v.reduce((a, b) => a + b, 0);
          return (
            <div key={p.id} className="bar-row">
              <span className="bar-name"><i className="sw" style={{ background: p.color }} />{p.name}</span>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${(total / max) * 100}%` }}>
                  {v.map((part, i) => part > 0 && (
                    <div key={i} className="bar-seg" style={{ flexGrow: part, background: series[i].color }}
                      onPointerMove={(e) => setTip({ text: `${p.name} · ${series[i].label}: ${fmt(part)} (${Math.round((part / Math.max(1, total)) * 100)}%)`, x: e.clientX, y: e.clientY })} />
                  ))}
                </div>
              </div>
              <span className="bar-total">{fmt(total)}</span>
            </div>
          );
        })}
      </div>
      {tip && <div className="chart-tip fixed" style={{ left: tip.x + 12, top: tip.y + 12 }}>{tip.text}</div>}
    </div>
  );
}

/** Two bars per player side by side (kills and losses: not parts of a whole, so not stacked). */
function GroupedBars({ title, players, series, values }: { title: string; players: PlayerInfo[]; series: { label: string; color: string }[]; values: (p: PlayerInfo) => number[] }) {
  const max = Math.max(1, ...players.flatMap((p) => values(p)));
  return (
    <div className="chart-block">
      <div className="row spread"><h3>{title}</h3><Legend series={series} /></div>
      <div className="bars">
        {players.map((p) => (
          <div key={p.id} className="bar-row grouped">
            <span className="bar-name"><i className="sw" style={{ background: p.color }} />{p.name}</span>
            <div className="bar-group">
              {values(p).map((v, i) => (
                <div key={i} className="bar-track thin" title={`${p.name} · ${series[i].label}: ${fmt(v)}`}>
                  <div className="bar-fill" style={{ width: `${(v / max) * 100}%` }}><div className="bar-seg" style={{ flexGrow: 1, background: series[i].color }} /></div>
                  <span className="bar-total">{fmt(v)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ series }: { series: { label: string; color: string }[] }) {
  return <div className="legend">{series.map((s) => <span key={s.label}><i className="sw" style={{ background: s.color }} />{s.label}</span>)}</div>;
}

function PlayerDetail({ history, player }: { history: MatchHistory; player: PlayerInfo }) {
  const samples = history.samples;
  const last = samples[samples.length - 1].players[player.id];
  const peak = (f: (p: PlayerSample) => number) => Math.max(...samples.map((s) => f(s.players[player.id])));
  const ages = history.ageTimes[player.id] ?? [];
  const rows: [string, string][] = [
    ['Result', player.winner ? 'Winner' : player.defeated ? 'Defeated' : 'Survived'],
    ['Final score', fmt(last.score.total)],
    ['Resources gathered', fmt(last.gathered.reduce((a, b) => a + b, 0))],
    ...RES_SERIES.map((r, i) => [`  ${r.label}`, fmt(last.gathered[i])] as [string, string]),
    ['Peak population', fmt(peak((p) => p.pop))],
    ['Peak villagers', fmt(peak((p) => p.villagers))],
    ['Peak military units', fmt(peak((p) => p.military))],
    ['Buildings standing', fmt(last.buildings)],
    ['Technologies researched', fmt(last.techs)],
    ['Kills', fmt(last.kills)],
    ['Units lost', fmt(last.losses)],
    ...AGE_NAMES.slice(1).map((name, i) => [`Reached ${name}`, ages[i + 1] !== undefined ? fmtTime(ages[i + 1]) : '-'] as [string, string]),
  ];
  return (
    <div className="results-detail">
      <table className="stat-table">
        <tbody>{rows.map(([k, v]) => <tr key={k}><th className={k.startsWith('  ') ? 'indent' : ''}>{k.trim()}</th><td>{v}</td></tr>)}</tbody>
      </table>
      <div>
        <h3>Villagers and army over time</h3>
        <LineChart width={480} samples={samples} label={`${player.name}: villagers and military units over time`} series={[
          { id: 'v', name: 'Villagers', color: '#199e70', get: (s) => s.players[player.id].villagers },
          { id: 'm', name: 'Military', color: '#d95926', get: (s) => s.players[player.id].military },
        ]} />
        <Legend series={[{ label: 'Villagers', color: '#199e70' }, { label: 'Military units', color: '#d95926' }]} />
      </div>
    </div>
  );
}

/** Explore the match: each player's figures over time, final totals, and one player in detail. */
export function Results({ history, players, onClose }: { history: MatchHistory; players: PlayerInfo[]; onClose: () => void }) {
  const [tab, setTab] = useState<'time' | 'totals' | 'player'>('time');
  const [metric, setMetric] = useState(METRICS[0]);
  const [shown, setShown] = useState(() => new Set(players.map((p) => p.id)));
  const [focus, setFocus] = useState(players[0]?.id ?? 0);
  const final = useMemo(() => history.samples[history.samples.length - 1], [history.samples]);
  if (!final) {
    return (
      <div className="overlay"><div className="menu-card"><p>No results were recorded for this match.</p><button className="menu-btn" onClick={onClose}>Close</button></div></div>
    );
  }
  const toggle = (id: number) => {
    const next = new Set(shown);
    if (next.has(id) && next.size > 1) next.delete(id);
    else next.add(id);
    setShown(next);
  };
  const visible = players.filter((p) => shown.has(p.id));
  const fp = players.find((p) => p.id === focus) ?? players[0];
  return (
    <div className="overlay results-overlay">
      <div className="menu-card results-card">
        <div className="row spread">
          <h2>Match Results</h2>
          <span className="muted small">Game length {fmtTime(final.t)}</span>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
        <div className="tabs">
          <button className={`tab ${tab === 'time' ? 'active' : ''}`} onClick={() => setTab('time')}>Over time</button>
          <button className={`tab ${tab === 'totals' ? 'active' : ''}`} onClick={() => setTab('totals')}>Final totals</button>
          <button className={`tab ${tab === 'player' ? 'active' : ''}`} onClick={() => setTab('player')}>By player</button>
        </div>
        {tab !== 'player' ? (
          <div className="chips">
            {players.map((p) => (
              <button key={p.id} className={`chip ${shown.has(p.id) ? 'on' : ''}`} onClick={() => toggle(p.id)} aria-pressed={shown.has(p.id)}>
                <i className="sw" style={{ background: p.color }} />{p.name}{p.winner ? ' (winner)' : ''}
              </button>
            ))}
          </div>
        ) : (
          <div className="chips">
            {players.map((p) => (
              <button key={p.id} className={`chip ${focus === p.id ? 'on' : ''}`} onClick={() => setFocus(p.id)} aria-pressed={focus === p.id}>
                <i className="sw" style={{ background: p.color }} />{p.name} <span className="muted">{p.civ}</span>
              </button>
            ))}
          </div>
        )}
        {tab === 'time' && (
          <>
            <div className="chips metrics">
              {METRICS.map((m) => <button key={m.key} className={`tab ${metric.key === m.key ? 'active' : ''}`} onClick={() => setMetric(m)}>{m.label}</button>)}
            </div>
            <LineChart samples={history.samples} label={`${metric.label} over time for each player`}
              series={visible.map((p) => ({ id: String(p.id), name: p.name, color: p.color, get: (sm: Sample) => metric.get(sm.players[p.id]) }))} />
            <div className="legend">{visible.map((p) => <span key={p.id}><i className="sw" style={{ background: p.color }} />{p.name}</span>)}</div>
          </>
        )}
        {tab === 'totals' && (
          <>
            <StackedBars title="Score" players={visible} series={SCORE_SERIES.map((s) => ({ label: s.label, color: s.color }))}
              values={(p) => SCORE_SERIES.map((s) => final.players[p.id].score[s.key])} />
            <StackedBars title="Resources gathered" players={visible} series={RES_SERIES} values={(p) => final.players[p.id].gathered} />
            <GroupedBars title="Kills and losses" players={visible} series={KILL_SERIES} values={(p) => [final.players[p.id].kills, final.players[p.id].losses]} />
          </>
        )}
        {tab === 'player' && fp && <PlayerDetail history={history} player={fp} />}
      </div>
    </div>
  );
}
